import React, { useState, useEffect } from "react";
import { useTheme, THEMES } from "@/contexts/ThemeContext";
import { useI18n, LANGUAGES } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { Moon, Sun, ArrowsHorizontal, User, Palette, Sparkle, BellSimple, Shield, Globe, FloppyDisk } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { GlassCard } from "@/components/glass/GlassCard";
import { PageShell } from "@/components/layout/AppLayout";
import api from "@/lib/api";
import { toast } from "sonner";

export default function Settings() {
  const { theme, setTheme, mode, setMode, rtl, setRtl } = useTheme();
  const { user } = useAuth();
  const { lang, changeLang } = useI18n();
  const [profile, setProfile] = useState(null);
  const [fullName, setFullName] = useState("");

  const load = () => api.get("/profile").then(({ data }) => {
    setProfile(data);
    setFullName(data.user.full_name);
  });
  useEffect(() => { load(); }, []);

  const saveProfile = async () => {
    await api.patch("/profile", { full_name: fullName });
    toast.success("Profile saved");
    load();
  };

  const saveNotif = async (key, value) => {
    if (!profile) return;
    const newNotifs = { ...profile.preferences.notifications, [key]: value };
    await api.patch("/profile", { notifications: newNotifs });
    setProfile({ ...profile, preferences: { ...profile.preferences, notifications: newNotifs } });
  };

  if (!profile) return <PageShell title="Settings"><div className="h-64 glass animate-pulse" /></PageShell>;

  return (
    <PageShell title="Settings" subtitle="Personalize your Clariva experience">
      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList className="glass-subtle p-1 flex-wrap h-auto">
          <TabsTrigger value="profile" data-testid="settings-tab-profile">Profile</TabsTrigger>
          <TabsTrigger value="appearance" data-testid="settings-tab-appearance">Appearance</TabsTrigger>
          <TabsTrigger value="language" data-testid="settings-tab-language">Language</TabsTrigger>
          <TabsTrigger value="notifications" data-testid="settings-tab-notifications">Notifications</TabsTrigger>
          <TabsTrigger value="security" data-testid="settings-tab-security">Security</TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          <GlassCard className="p-6 max-w-2xl">
            <div className="flex items-center gap-3 mb-5">
              <User size={20} weight="duotone" className="text-[var(--accent)]" />
              <h2 className="text-lg font-medium" style={{ fontFamily: 'var(--font-heading)' }}>Your profile</h2>
            </div>
            <div className="space-y-4">
              <div>
                <div className="overline mb-1">Full name</div>
                <Input value={fullName} onChange={(e) => setFullName(e.target.value)} data-testid="settings-name-input" className="glass-subtle border-[var(--border)]" />
              </div>
              <div>
                <div className="overline mb-1">Email</div>
                <Input value={profile.user.email} readOnly className="glass-subtle border-[var(--border)] font-mono text-xs opacity-70" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="overline mb-1">Role</div>
                  <div className="text-sm font-medium mt-2 capitalize">{profile.user.role}</div>
                </div>
                <div>
                  <div className="overline mb-1">Organization</div>
                  <div className="text-sm font-medium mt-2">{profile.tenant?.name}</div>
                </div>
              </div>
              <Button onClick={saveProfile} data-testid="settings-profile-save" className="gap-2"><FloppyDisk size={14} /> Save profile</Button>

              <div className="pt-4 mt-2 border-t border-[var(--border)]">
                <div className="overline mb-1">Security</div>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="text-sm text-[var(--text-3)]">
                    Change the password you use to sign in to Clariva.
                  </div>
                  <a
                    href="/change-password"
                    className="text-sm font-medium text-[var(--accent)] hover:underline inline-flex items-center gap-1"
                    data-testid="settings-change-password-link"
                  >
                    <Shield size={14} /> Change password
                  </a>
                </div>
              </div>
            </div>
          </GlassCard>
        </TabsContent>

        <TabsContent value="appearance">
          <GlassCard className="p-6">
            <div className="flex items-center gap-3 mb-5">
              <Palette size={20} weight="duotone" className="text-[var(--accent)]" />
              <h2 className="text-lg font-medium" style={{ fontFamily: 'var(--font-heading)' }}>Appearance</h2>
            </div>

            <div className="mb-6">
              <div className="overline mb-3">Theme pack</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {THEMES.map((t) => (
                  <button key={t.id} onClick={() => setTheme(t.id)} data-testid={`settings-theme-${t.id}`} className={`text-left p-4 rounded-lg border transition ${theme === t.id ? "border-[var(--accent)] bg-[var(--surface-2)]" : "border-[var(--border)] hover:border-[var(--border-strong)]"}`}>
                    <div className="flex gap-1 mb-3">
                      <span className="w-8 h-8 rounded-md border border-[var(--border)]" style={{ background: t.swatch }} />
                      <span className="w-8 h-8 rounded-md" style={{ background: t.accent }} />
                    </div>
                    <div className="text-sm font-medium">{t.name}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between py-3 border-t border-[var(--border)]">
              <div className="flex items-center gap-3">
                {mode === "dark" ? <Moon size={18} /> : <Sun size={18} />}
                <div>
                  <div className="text-sm font-medium">Dark mode</div>
                  <div className="text-xs text-[var(--text-3)]">Easier on the eyes late at night</div>
                </div>
              </div>
              <Switch checked={mode === "dark"} onCheckedChange={(v) => setMode(v ? "dark" : "light")} data-testid="settings-dark-toggle" />
            </div>

            <div className="flex items-center justify-between py-3 border-t border-[var(--border)]">
              <div className="flex items-center gap-3">
                <ArrowsHorizontal size={18} />
                <div>
                  <div className="text-sm font-medium">Right-to-left layout</div>
                  <div className="text-xs text-[var(--text-3)]">For Arabic, Hebrew, Urdu</div>
                </div>
              </div>
              <Switch checked={rtl} onCheckedChange={setRtl} data-testid="settings-rtl-toggle" />
            </div>
          </GlassCard>
        </TabsContent>

        <TabsContent value="language">
          <GlassCard className="p-6 max-w-2xl">
            <div className="flex items-center gap-3 mb-5">
              <Globe size={20} weight="duotone" className="text-[var(--accent)]" />
              <h2 className="text-lg font-medium" style={{ fontFamily: 'var(--font-heading)' }}>Language & region</h2>
            </div>
            <div className="space-y-4">
              <div>
                <div className="overline mb-1">Interface language</div>
                <Select value={lang} onValueChange={changeLang}>
                  <SelectTrigger data-testid="settings-language-select" className="glass-subtle border-[var(--border)]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LANGUAGES.map((l) => (
                      <SelectItem key={l.code} value={l.code}>
                        <div className="flex items-center gap-2">
                          <span>{l.native}</span>
                          <span className="text-[var(--text-3)] text-xs">{l.name}</span>
                          {l.rtl && <span className="chip chip-neutral text-[9px]">RTL</span>}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="text-xs text-[var(--text-3)] mt-2">AI responses remain in the contract's original language. Some UI strings may fall back to English.</div>
              </div>
              <div>
                <div className="overline mb-1">Timezone</div>
                <Select value={profile.preferences.timezone} onValueChange={async (v) => { await api.patch("/profile", { timezone: v }); load(); toast.success("Timezone updated"); }}>
                  <SelectTrigger data-testid="settings-timezone-select" className="glass-subtle border-[var(--border)]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["UTC", "America/New_York", "America/Los_Angeles", "Europe/London", "Europe/Paris", "Asia/Dubai", "Asia/Singapore", "Asia/Tokyo"].map((tz) => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </GlassCard>
        </TabsContent>

        <TabsContent value="notifications">
          <GlassCard className="p-6 max-w-2xl">
            <div className="flex items-center gap-3 mb-5">
              <BellSimple size={20} weight="duotone" className="text-[var(--accent)]" />
              <h2 className="text-lg font-medium" style={{ fontFamily: 'var(--font-heading)' }}>Notifications & AI alerts</h2>
            </div>
            {[
              { k: "obligations_email", label: "Obligation deadlines (email)", desc: "Email 7 days before any SLA or obligation is due" },
              { k: "breach_prediction", label: "AI breach prediction", desc: "Alert when Clariva predicts a contract is about to breach" },
              { k: "renewal_warnings", label: "Auto-renewal warnings", desc: "Notify 90 days before auto-renewal windows open" },
              { k: "weekly_briefing", label: "Weekly audio briefing", desc: "Monday morning portfolio summary (voiced by OpenAI TTS)" },
              { k: "in_app", label: "In-app notifications", desc: "Show the notification bell with live alerts" },
              { k: "sms", label: "SMS alerts (critical)", desc: "Text-message for critical/breached obligations" },
            ].map((n) => (
              <div key={n.k} className="flex items-center justify-between py-3 border-t border-[var(--border)]">
                <div>
                  <div className="text-sm font-medium">{n.label}</div>
                  <div className="text-xs text-[var(--text-3)]">{n.desc}</div>
                </div>
                <Switch checked={profile.preferences.notifications?.[n.k] || false} onCheckedChange={(v) => saveNotif(n.k, v)} data-testid={`settings-notif-${n.k}`} />
              </div>
            ))}
          </GlassCard>
        </TabsContent>

        <TabsContent value="security">
          <GlassCard className="p-6 max-w-2xl">
            <div className="flex items-center gap-3 mb-5">
              <Shield size={20} weight="duotone" className="text-[var(--accent)]" />
              <h2 className="text-lg font-medium" style={{ fontFamily: 'var(--font-heading)' }}>Security & compliance</h2>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><div className="overline">Data residency</div><div className="mt-1">EU (Frankfurt)</div></div>
              <div><div className="overline">Encryption</div><div className="mt-1">AES-256, TLS 1.3</div></div>
              <div><div className="overline">Audit certification</div><div className="mt-1">SOC 2 Type II</div></div>
              <div><div className="overline">Compliance</div><div className="mt-1">GDPR, eIDAS</div></div>
              <div><div className="overline">2FA</div><div className="mt-1">Email OTP</div></div>
              <div><div className="overline">Session timeout</div><div className="mt-1">24 hours</div></div>
            </div>
            <div className="mt-6 text-xs text-[var(--text-3)]">Need SAML SSO, BYOK, or a regional deployment? <a href="/app/sso" className="text-[var(--accent)] hover:underline">Configure SSO →</a></div>
          </GlassCard>
        </TabsContent>
      </Tabs>
    </PageShell>
  );
}
